import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import queryClient from "api/queryClient.ts";
import { tenantApi } from "api/Tenant/api.ts";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import StoreTenantRequest = App.Http.Requests.Tenant.StoreTenantRequest;
import UpdateTenantRequest = App.Http.Requests.Tenant.UpdateTenantRequest;
import RequestAccessRequest = App.Http.Requests.Tenant.RequestAccessRequest;
import RequestTenantDeletionRequest = App.Http.Requests.Tenant.RequestTenantDeletionRequest;
import ConfirmTenantDeletionRequest = App.Http.Requests.Tenant.ConfirmTenantDeletionRequest;
import GetMyTenantsRequest = App.Http.Requests.Tenant.GetMyTenantsRequest;
import GetTenantRequest = App.Http.Requests.Tenant.GetTenantRequest;
import GetCatalogTenantsRequest = App.Http.Requests.Tenant.GetCatalogTenantsRequest;
import GetCatalogTenantRequest = App.Http.Requests.Tenant.GetCatalogTenantRequest;
import GetTenantResponse = App.Http.Responses.Tenant.GetTenantResponse;
import GetTenantsResponse = App.Http.Responses.Tenant.GetTenantsResponse;
import GetMyTenantsResponse = App.Http.Responses.Tenant.GetMyTenantsResponse;
import GetSlugAvailableResponse = App.Http.Responses.Tenant.GetSlugAvailableResponse;
import GetCreationStatusResponse = App.Http.Responses.Tenant.GetCreationStatusResponse;
import RequestAccessResponse = App.Http.Responses.Tenant.RequestAccessResponse;

// Hook to get list of tenants
export const useGetTenantsQuery = (options = {}) => {
    return useQueryApi<GetTenantsResponse>({
        queryKey: ["getTenants"],
        queryFn: () => tenantApi.index(),
        ...options,
    });
};

// Hook to store a tenant
export const useStoreTenantMutation = (
    options: UseMutationOptions<
        GetTenantResponse | GetCreationStatusResponse,
        AxiosError<ApiErrorResponse>,
        StoreTenantRequest
    > = {}
) => {
    return useMutationApi<GetTenantResponse | GetCreationStatusResponse, StoreTenantRequest>({
        mutationKey: ["storeTenant"],
        mutationFn: (request: StoreTenantRequest) => tenantApi.store(request),
        ...options,
    });
};

// Hook to get a specific tenant
export const useGetTenantQuery = (tenantId: string, request?: GetTenantRequest, options = {}) => {
    return useQueryApi<GetTenantResponse>({
        queryKey: ["getTenant", tenantId, request],
        queryFn: () => tenantApi.show(tenantId, request),
        ...options,
    });
};

// Hook to update a tenant
export const useUpdateTenantMutation = (
    options: UseMutationOptions<
        GetTenantResponse,
        AxiosError<ApiErrorResponse>,
        { updateTenantRequest: UpdateTenantRequest; tenantId: string }
    > = {}
) => {
    return useMutationApi<GetTenantResponse, { updateTenantRequest: UpdateTenantRequest; tenantId: string }>({
        mutationKey: ["updateTenant"],
        mutationFn: ({ updateTenantRequest, tenantId }) => tenantApi.update(updateTenantRequest, tenantId),
        ...options,
    });
};

// Hook to delete a tenant
export const useDestroyTenantMutation = (
    options: UseMutationOptions<null, AxiosError<ApiErrorResponse>, string> = {}
) => {
    return useMutationApi<null, string>({
        mutationKey: ["destroyTenant"],
        mutationFn: (tenantId: string) => tenantApi.destroy(tenantId),
        ...options,
    });
};

// Hook to check slug availability
export const useSlugAvailableQuery = (name: string, options = {}) => {
    return useQueryApi<GetSlugAvailableResponse>({
        queryKey: ["slugAvailable", name],
        queryFn: () => tenantApi.slugAvailable(name),
        ...options,
    });
};

// Hook to get tenant catalog (public tenants)
export const useGetTenantCatalogQuery = (request: GetCatalogTenantsRequest, options = {}) => {
    return useQueryApi<GetTenantsResponse>({
        queryKey: ["getTenantCatalog", { ...request }],
        queryFn: () => tenantApi.catalog(request),
        ...options,
    });
};

// Hook to get a single tenant from the public catalog (accessible regardless of membership)
export const useGetTenantCatalogShowQuery = (tenantId: string, request?: GetCatalogTenantRequest, options = {}) => {
    return useQueryApi<GetTenantResponse>({
        queryKey: ["getTenantCatalogShow", tenantId, request],
        queryFn: () => tenantApi.catalogShow(tenantId, request),
        ...options,
    });
};

// Hook to get the authenticated user's access status for a tenant (works regardless of catalog visibility)
export const useGetTenantAccessStatusQuery = (tenantId: string, options = {}) => {
    return useQueryApi<GetTenantResponse>({
        queryKey: ["getTenantAccessStatus", tenantId],
        queryFn: () => tenantApi.accessStatus(tenantId),
        ...options,
    });
};

// Hook to get my tenants (active programs)
export const useGetMyTenantsQuery = (request: GetMyTenantsRequest, options = {}) => {
    return useQueryApi<GetMyTenantsResponse>({
        queryKey: ["getMyTenants", { ...request }],
        queryFn: () => tenantApi.myTenants(request),
        ...options,
    });
};

// Hook to leave a tenant
export const useLeaveTenantMutation = (
    options: UseMutationOptions<void, AxiosError<ApiErrorResponse>, string> = {}
) => {
    return useMutationApi<void, string>({
        mutationKey: ["leaveTenant"],
        mutationFn: (tenantId: string) => tenantApi.leaveTenant(tenantId),
        ...options,
        onSuccess: (data, variables, context) => {
            options.onSuccess?.(data, variables, context);
            return queryClient.invalidateQueries({ queryKey: ["getMyTenants"] });
        },
    });
};

// Hook to poll tenant creation status
export const useCreationStatusQuery = (tenantId: string | null, options = {}) => {
    return useQueryApi<GetCreationStatusResponse>({
        queryKey: ["getCreationStatus", tenantId],
        queryFn: () => tenantApi.creationStatus(tenantId!),
        refetchInterval: 2000,
        ...options,
        enabled: !!tenantId,
    });
};

// Hook to request tenant deletion
export const useRequestTenantDeletionMutation = (
    options: UseMutationOptions<
        void,
        AxiosError<ApiErrorResponse>,
        { tenantId: string; request: RequestTenantDeletionRequest }
    > = {}
) => {
    return useMutationApi<void, { tenantId: string; request: RequestTenantDeletionRequest }>({
        mutationKey: ["requestTenantDeletion"],
        mutationFn: ({ tenantId, request }) => tenantApi.requestDeletion(tenantId, request),
        ...options,
        onSuccess: (data, variables, context) => {
            options.onSuccess?.(data, variables, context);
            queryClient.invalidateQueries({ queryKey: ["getTenant", variables.tenantId] });
            return queryClient.invalidateQueries({ queryKey: ["getMyTenants"] });
        },
    });
};

// Hook to confirm tenant deletion via signed link
export const useConfirmTenantDeletionMutation = (
    options: UseMutationOptions<void, AxiosError<ApiErrorResponse>, ConfirmTenantDeletionRequest> = {}
) => {
    return useMutationApi<void, ConfirmTenantDeletionRequest>({
        mutationKey: ["confirmTenantDeletion"],
        mutationFn: (params: ConfirmTenantDeletionRequest) => tenantApi.confirmDeletion(params),
        ...options,
    });
};

// Hook to request access to a public tenant (catalog "Apply" / "Request role")
export const useRequestAccessMutation = (
    options: UseMutationOptions<
        RequestAccessResponse,
        AxiosError<ApiErrorResponse>,
        { tenantId: string; request?: RequestAccessRequest }
    > = {}
) => {
    return useMutationApi<RequestAccessResponse, { tenantId: string; request?: RequestAccessRequest }>({
        mutationKey: ["requestAccess"],
        mutationFn: ({ tenantId, request }) => tenantApi.requestAccess(tenantId, request),
        ...options,
        onSuccess: (data, variables, context) => {
            options.onSuccess?.(data, variables, context);
            queryClient.invalidateQueries({ queryKey: ["getTenantCatalog"] });
            return queryClient.invalidateQueries({ queryKey: ["getTenantAccessStatus", variables.tenantId] });
        },
    });
};
